import React from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface DeleteMetricDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metricName: string;
  metricId: string;
  isDeleting: boolean;
  onConfirmDelete: () => Promise<void>;
}

const DeleteMetricDialog: React.FC<DeleteMetricDialogProps> = ({
  open,
  onOpenChange,
  metricName,
  metricId,
  isDeleting,
  onConfirmDelete
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Confirm Metric Deletion
          </DialogTitle>
          <DialogDescription>
            This action will permanently remove this metric from the system.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="space-y-4">
            <p>
              Are you sure you want to delete the metric <span className="font-semibold">{metricName}</span>?
            </p>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-700">
                <span className="font-medium">Warning:</span> This will:
              </p>
              <ul className="text-sm text-amber-700 list-disc list-inside mt-1">
                <li>Remove the metric from all plans it's associated with</li>
                <li>Delete all pricing data associated with this metric</li>
                <li>Remove the metric ID: <span className="font-mono text-xs">{metricId}</span></li>
              </ul>
            </div>
          </div>
        </div>
        
        <DialogFooter className="sm:justify-between flex-row-reverse">
          <Button
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              "Delete Metric"
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteMetricDialog;
